import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { ForwardingNumber } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-numbers/phone_numbers_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  SkeletonLoaders,
  useAlert,
  useForm,
  Button,
  Heading,
  useModalControl,
  Modal,
  Text,
} from '@frontend/design-system';
import { AddForwardingNumberModal } from '../../../add-forwarding-number-modal';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';

export const ForwardingNumberStep = ({
  callRoute,
  tenantId,
  initialState,
  settingsTenantLocation,
  callObjectsData,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { modalProps: infoModalProps, openModal: openInfoModal, closeModal: closeInfoModal } = useModalControl();

  const { modalProps: createModalProps, triggerProps: createTriggerProps } = useModalControl();

  const { updateQuery } = PhoneNumbersV1._QueryUpdaters.useQueryUpdaters();

  const { forwardingNumbers, forwardingNumbersIsLoading, routes, allPhoneNumbers, allPhoneNumbersIsLoading } =
    callObjectsData;
  const { getLocationName, selectedLocationIds } = useAppScopeStore();
  const locationName = getLocationName(callRoute?.locationId);
  const isSingleLocation = selectedLocationIds.length === 1;
  const phoneNumbersData = allPhoneNumbers || [];
  const isLoadingPhoneNumbers = allPhoneNumbersIsLoading;

  const voicePhoneNumbers = phoneNumbersData.filter((p) => p.capabilities?.voiceProvider === true);
  const groupedPhoneNumbers = useMemo(() => {
    // Check if the saved caller number is spoofed (doesn't exist in voicePhoneNumbers)
    const savedCallerNumber = initialState?.callObject?.forwardingNumberExpansion?.callerNumber;
    const isSavedNumberSpoofed =
      savedCallerNumber &&
      savedCallerNumber !== '0' &&
      !voicePhoneNumbers.some((phone) => phone.phoneNumber?.nationalNumber?.toString() === savedCallerNumber);

    const grouped = voicePhoneNumbers.reduce<{
      spoofedNumbers: Array<{ id: string; phoneNumber: { nationalNumber: string }; isSpoofed: true }>;
      callRouteNumbers: typeof voicePhoneNumbers;
      thisLocationNumbers: typeof voicePhoneNumbers;
      locationGroups: Record<string, { numbers: typeof voicePhoneNumbers; locationName: string }>;
    }>(
      (acc, phoneNumber) => {
        // Check if this number is assigned to the current call route
        const currentRoute = routes?.find((route) => route.callRouteId === callRoute?.callRouteId);
        const isCallRouteNumber = currentRoute?.phoneNumberIds?.includes(phoneNumber.id) ?? false;

        if (isCallRouteNumber) {
          acc.callRouteNumbers.push(phoneNumber);
        } else if (phoneNumber.locationId === settingsTenantLocation?.id && isSingleLocation) {
          acc.thisLocationNumbers.push(phoneNumber);
        } else if (locationName && !isSingleLocation && getLocationName(phoneNumber.locationId) === locationName) {
          acc.thisLocationNumbers.push(phoneNumber);
        } else if (selectedLocationIds.includes(phoneNumber.locationId || '')) {
          const locationId = phoneNumber.locationId || 'unknown';
          const locationName = getLocationName(locationId) || `Location ${locationId}`;

          if (!acc.locationGroups[locationId]) {
            acc.locationGroups[locationId] = {
              numbers: [],
              locationName: locationName,
            };
          }
          acc.locationGroups[locationId].numbers.push(phoneNumber);
        } else {
          // Group by location ID for other locations
          const locationId = phoneNumber.locationId || 'unknown';
          const locationName = getLocationName(locationId) || `Location ${locationId}`;

          if (!acc.locationGroups[locationId]) {
            acc.locationGroups[locationId] = {
              numbers: [],
              locationName: locationName,
            };
          }
          acc.locationGroups[locationId].numbers.push(phoneNumber);
        }

        return acc;
      },
      { spoofedNumbers: [], callRouteNumbers: [], thisLocationNumbers: [], locationGroups: {} }
    );

    // Add the spoofed number if it exists and is currently selected
    if (isSavedNumberSpoofed) {
      grouped.spoofedNumbers.push({
        id: `spoofed-${savedCallerNumber}`,
        phoneNumber: { nationalNumber: savedCallerNumber },
        isSpoofed: true,
      });
    }

    return grouped;
  }, [
    voicePhoneNumbers,
    settingsTenantLocation?.id,
    routes,
    callRoute?.callRouteId,
    initialState?.callObject?.forwardingNumberExpansion?.callerNumber,
  ]);

  const { getFieldProps, isComplete, values } = useForm({
    fields: {
      forwardingNumberId: {
        required: true,
        type: 'dropdown',
        value: initialState?.callObject?.primitiveId,
      },
      ringDuration: {
        type: 'dropdown',
        required: true,
        value: String(initialState?.callObject?.forwardingNumberExpansion?.timeout ?? '0'),
      },
      phoneNumbers: {
        required: true,
        type: 'dropdown',
        value: initialState?.callObject?.forwardingNumberExpansion?.callerNumber || '0',
      },
    },
  });

  const locationId = settingsTenantLocation?.id ?? '';

  const handleProceedClick = useCallback(() => {
    const number = forwardingNumbers.find((number) => number?.id === values.forwardingNumberId);

    if (!number) {
      alerts.error('Unknown forwarding number data');
      sentry.error({
        error: 'Could not find forwarding number by ID in DeviceStep',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            forwardingNumbers,
            forwardingNumberId: values.forwardingNumberId,
          },
        },
      });
      return;
    }

    const name = getForwardingNumberDisplayName(number);

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER,
      callObject: {
        primitiveId: number.id,
        primitiveName: name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        forwardingNumberExpansion: {
          ...initialState?.callObject?.forwardingNumberExpansion,
          timeout: Number(values.ringDuration),
          callerNumber: values.phoneNumbers,
        },
      },
    });
  }, [forwardingNumbers, values.forwardingNumberId, values.ringDuration, alerts, onProceedClick, initialState]);

  const getForwardingNumberDisplayName = (forwardingNumber?: ForwardingNumber) =>
    forwardingNumber ? `${forwardingNumber.name} (${forwardingNumber.number})` : '';

  const handleOnCreateSuccess = (forwardingNumber: ForwardingNumberTypes.FwdNumberModel) => {
    const ForwardingNumberID = forwardingNumber.id;

    // @ts-expect-error This is complaining thinking that name and value do not exist on the event target,
    // but they do.
    getFieldProps('forwardingNumberId').onChange({ target: { name: 'forwardingNumberId', value: ForwardingNumberID } });

    updateQuery({
      endpointName: 'ListForwardingNumbers',
      updater: (oldData) => {
        const updated = {
          forwardingNumbers: [
            ...(oldData?.forwardingNumbers ?? []),
            // Add the new forwarding number to the list.
            // NOTE: the data returned from the API in the mutation is from a different service than the service
            // that returns the data from the list endpoint and so the data here needs to be transformed.
            {
              id: forwardingNumber.id,
              name: forwardingNumber.name,
              number: forwardingNumber.number,
            },
          ],
        };

        return updated;
      },
    });
  };

  return (
    <>
      <Step
        title={t('Forwarding Number')}
        primaryButtonDisabled={!isComplete}
        onPrimaryButtonClick={handleProceedClick}
      >
        <SkeletonLoaders.Loader isLoading={forwardingNumbersIsLoading} shape='rectangle' width={320} height={50}>
          <DropdownField
            label={t('Select Forwarding Number')}
            {...getFieldProps('forwardingNumberId')}
            placeholder={t('Select Forwarding Number')}
            css={css`
              max-width: 318px;
              margin-top: ${theme.spacing(1)};
            `}
          >
            <DropdownField.Option
              css={css`
                padding: 0;
              `}
              key={'create-forwarding-number'}
              value=''
              isSelectable={false}
            >
              <Button
                iconName='plus'
                variant='tertiary'
                onClick={() => createTriggerProps.onClick()}
                css={css`
                  width: 100%;
                  justify-content: start;
                `}
              >
                {t('Add Forwarding Number')}
              </Button>
            </DropdownField.Option>
            {forwardingNumbers.map((number) => (
              <DropdownField.Option key={number.id} value={number.id}>
                {getForwardingNumberDisplayName(number)}
              </DropdownField.Option>
            ))}
          </DropdownField>
        </SkeletonLoaders.Loader>

        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <Heading level={3}>{t('Ring Duration')}</Heading>
          <Button iconName='info-small' variant='secondary' onClick={openInfoModal} />
        </div>
        <SkeletonLoaders.Loader isLoading={forwardingNumbersIsLoading} shape='rectangle' width={320} height={50}>
          <DropdownField
            label={t('Ring Duration')}
            {...getFieldProps('ringDuration')}
            placeholder={t('Ring Duration')}
            helperText={t('6 seconds usually equals 1 ring')}
            css={css`
              max-width: 318px;
              margin-top: ${theme.spacing(1)};
            `}
          >
            {[6_000, 12_000, 18_000, 24_000, 30_000, 36_000, 42_000, 48_000, 54_000, 60_000].map((duration) => (
              <DropdownField.Option key={duration} value={String(duration)}>
                {`${t('{{duration}} seconds', { duration: duration / 1000 })}`}
              </DropdownField.Option>
            ))}
            <DropdownField.Option key={'0'} value={'0'}>
              {t('Ring until answered')}
            </DropdownField.Option>
          </DropdownField>
        </SkeletonLoaders.Loader>

        <div css={{ maxWidth: 318, display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          <Heading level={3}>{t('Caller ID on Forwarded Calls')}</Heading>
          <Text size='small' color='subdued'>
            {t(
              'Choose whether forwarded calls show your office number or the caller’s number. Using your office number helps the call recipient recognize business-related calls and avoid confusion. '
            )}
          </Text>
        </div>
        <SkeletonLoaders.Loader isLoading={isLoadingPhoneNumbers} shape='rectangle' width={320} height={50}>
          <DropdownField
            label={t('Forwarded Caller ID Number')}
            {...getFieldProps('phoneNumbers')}
            placeholder={t('Forwarded Caller ID Number')}
            css={css`
              max-width: 318px;
              margin-top: ${theme.spacing(1)};
            `}
            data-trackingid='forwarding-number-step-caller-id-dropdown'
          >
            <DropdownField.Option key={'0'} value={'0'}>
              {t('Use Caller Number (Default)')}
            </DropdownField.Option>

            {groupedPhoneNumbers.spoofedNumbers.map((number) => (
              <DropdownField.Option key={number.id} value={number.phoneNumber?.nationalNumber?.toString() ?? ''}>
                {formatPhoneNumber(number.phoneNumber?.nationalNumber?.toString() ?? '')} (Spoofed)
              </DropdownField.Option>
            ))}

            {groupedPhoneNumbers.callRouteNumbers.length > 0 && (
              <DropdownField.OptionGroup label={t('Numbers assigned to this Call Route')}>
                {groupedPhoneNumbers.callRouteNumbers.map((number) => (
                  <DropdownField.Option key={number.id} value={number.phoneNumber?.nationalNumber?.toString() ?? ''}>
                    {formatPhoneNumber(number.phoneNumber?.nationalNumber?.toString() ?? '')}
                  </DropdownField.Option>
                ))}
              </DropdownField.OptionGroup>
            )}

            {groupedPhoneNumbers.thisLocationNumbers.length > 0 && (
              <DropdownField.OptionGroup
                label={
                  isSingleLocation
                    ? groupedPhoneNumbers.callRouteNumbers.length
                      ? t('Other Numbers')
                      : t('Office Numbers')
                    : t('{{locationName}} (This Location)', {
                        locationName: locationName || 'Location',
                      })
                }
              >
                {groupedPhoneNumbers.thisLocationNumbers.map((number) => (
                  <DropdownField.Option key={number.id} value={number.phoneNumber?.nationalNumber?.toString() ?? ''}>
                    {formatPhoneNumber(number.phoneNumber?.nationalNumber?.toString() ?? '')}
                  </DropdownField.Option>
                ))}
              </DropdownField.OptionGroup>
            )}

            {Object.entries(groupedPhoneNumbers.locationGroups)
              .sort(([, a], [, b]) => a.locationName.localeCompare(b.locationName))
              .map(([locationId, locationGroup]) => (
                <DropdownField.OptionGroup key={locationId} label={locationGroup.locationName}>
                  {locationGroup.numbers.map((number) => (
                    <DropdownField.Option key={number.id} value={number.phoneNumber?.nationalNumber?.toString() ?? ''}>
                      {formatPhoneNumber(number.phoneNumber?.nationalNumber?.toString() ?? '')}
                    </DropdownField.Option>
                  ))}
                </DropdownField.OptionGroup>
              ))}
          </DropdownField>
        </SkeletonLoaders.Loader>
      </Step>

      <Modal {...infoModalProps} minWidth={400}>
        <Modal.Header onClose={closeInfoModal}>{t('Ring Duration')}</Modal.Header>
        <Modal.Body>
          <Text>
            {t(
              'The length of time a call rings before the system takes the caller to the next step in the Call Route.'
            )}
          </Text>
        </Modal.Body>
      </Modal>

      <Modal asChild {...createModalProps}>
        <AddForwardingNumberModal
          fwdNumbers={forwardingNumbers.map((item) => ({
            id: item.id,
            name: item.name,
            number: item.number,
            tenantId: tenantId,
          }))}
          disallowedNumbers={[]}
          locationID={locationId}
          onSuccess={handleOnCreateSuccess}
          closeModal={createModalProps.onClose}
        />
      </Modal>
    </>
  );
};
